import { derived, readable, writable, type Readable, type Writable } from 'svelte/store';
import { real_id } from 'src/helpers/interface_helpers';
import type { Device } from 'src/types/Device';
import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
import {
	rebooting_devices,
	updating_devices,
	wifi_updating_devices,
} from 'src/stores/device-runtime/process';
import { associating_devices } from './cloud-associating';
import { loadingData } from '../ui/loading-data';
import { pending_connection_devices, z_wave_inclusion_statuses } from './firmware-ui';
import {
	buildDeviceStateContext,
	computeDeviceState,
	defaultStateType,
	offlineStateType,
	resolveDeviceStateLookupId,
	type DeviceStateConfig,
} from './compute-device-state';
import {
	createListMembershipSelector,
	createRecordEntrySelector,
	createRecordKeyExistsSelector,
} from './store-selectors';
import { getDevice, getDeviceSettings, getDeviceStatus } from './status-selectors';

export {
	availableStatuses,
	createDefaultDeviceStateConfig,
	defaultStateType,
	offlineStateType,
	type DeviceStateConfig,
} from './compute-device-state';

const emptyDeviceStateStore: Readable<DeviceStateConfig | Record<string, never>> = readable({});

const deviceStateCache = new Map<string, Readable<DeviceStateConfig | Record<string, never>>>();
const deviceStateWithDelayCache = new Map<string, Readable<DeviceStateConfig | Record<string, never>>>();

const wifiUpdatingForDevice = (lookupId: string) =>
	derived(wifi_updating_devices, ($wifiUpdatingDevices) =>
		$wifiUpdatingDevices.some((entry) => Object.keys(entry)[0] === lookupId),
	);

function createDeviceStateStore(deviceId: string): Readable<DeviceStateConfig | Record<string, never>> {
	const deviceStore = getDevice(deviceId);

	return derived(deviceStore, ($device, set) => {
		if (!$device || Object.keys($device).length === 0) {
			set({});
			return;
		}

		const device = $device as Device;
		const lookupId = resolveDeviceStateLookupId(device);
		const statusStore = getDeviceStatus(real_id(lookupId));
		const settingsStore = getDeviceSettings(real_id(lookupId));

		const inner = derived(
			[
				loadingData,
				statusStore,
				settingsStore,
				createListMembershipSelector(rebooting_devices, lookupId),
				createListMembershipSelector(auth_locked_devices, lookupId),
				createListMembershipSelector(pending_connection_devices, lookupId),
				createRecordKeyExistsSelector(updating_devices, lookupId),
				createRecordEntrySelector(updating_devices, lookupId),
				createRecordEntrySelector(associating_devices, lookupId),
				wifiUpdatingForDevice(lookupId),
				derived(z_wave_inclusion_statuses, ($statuses) => $statuses[device.id]),
			],
			([
				$loadingData,
				$status,
				$settings,
				isRebooting,
				isLocked,
				isPendingMember,
				isUpdating,
				updatingEntry,
				associatingEntry,
				isUpdatingWifi,
				zWaveInclusionStatus,
			]) => {
				const hasConnectedData = $status?.online && typeof $settings !== 'undefined';

				const ctx = buildDeviceStateContext(device, {
					loadingData: $loadingData,
					status: $status,
					settings: $settings,
					isRebooting,
					isLocked,
					isPending: isPendingMember && !hasConnectedData,
					isUpdating,
					updatingEntry: updatingEntry as { prog?: number } | undefined,
					isAssociating: Boolean(associatingEntry),
					isUpdatingWifi,
					zWaveInclusionStatus,
				});

				return computeDeviceState(device, ctx);
			},
		);

		return inner.subscribe(set);
	});
}

/** Per-device state selector — only recomputes when this device's inputs change. */
export const getDeviceState = (
	deviceId: string | undefined,
): Readable<DeviceStateConfig | Record<string, never>> => {
	if (!deviceId) {
		return emptyDeviceStateStore;
	}

	let store = deviceStateCache.get(deviceId);
	if (!store) {
		store = createDeviceStateStore(deviceId);
		deviceStateCache.set(deviceId, store);
	}

	return store;
};

/** Count offline devices using per-device state selectors. */
export function getOfflineDeviceCount(deviceIds: string[] | undefined): Readable<number> {
	if (!deviceIds?.length) {
		return readable(0);
	}

	const stores = deviceIds.map((id) => getDeviceState(id));
	return derived(
		stores,
		($states) =>
			$states.filter((state) => (state as DeviceStateConfig)?.type === offlineStateType).length,
	);
}

function create_device_temp_state_data() {
	const { subscribe, set, update } = writable<{
		id?: string;
		delay?: boolean;
		type?: string;
	}>({});

	return {
		subscribe,
		set,
		update: (newState) =>
			update((data) => {
				return { ...data, [newState.id]: newState };
			}),
		remove: (id) =>
			update((data) => {
				delete data[id];
				return data;
			}),
		reset: () => set({}),
	};
}

const deviceTempStateData = create_device_temp_state_data();

function createDeviceStateWithDelayStore(deviceId: string, delay: number) {
	return derived(
		[getDeviceState(deviceId), deviceTempStateData, getDevice(deviceId)],
		([$deviceState, $deviceTempStateData, $device], set) => {
			const state = $deviceState as DeviceStateConfig;
			if (!state || Object.keys(state).length === 0) return set({});

			const isGenOne = +($device as Device)?.gen === 1;
			if (isGenOne) {
				return set(state);
			}
			const isDeviceStateDelay = $deviceTempStateData[deviceId]?.delay;
			const prevDeviceStateType = $deviceTempStateData[deviceId]?.type;

			if (!isDeviceStateDelay && prevDeviceStateType === 3 && state.type === defaultStateType) {
				deviceTempStateData.update({
					...$deviceTempStateData[deviceId],
					id: deviceId,
					delay: true,
				});

				setTimeout(() => {
					deviceTempStateData.update({
						id: deviceId,
						type: state.type,
						delay: false,
					});

					set(state);
				}, delay);
			} else if (!isDeviceStateDelay) {
				if (prevDeviceStateType !== state.type) {
					deviceTempStateData.update({
						...$deviceTempStateData[deviceId],
						type: state.type,
						id: deviceId,
					});
				}
				set(state);
			}
		},
		{},
	);
}

/** Returns a stable derived store per device id (avoids new subscriptions on each reactive run). */
export const getDeviceStateWithDelay = (deviceId: string | undefined, delay = 2500) => {
	if (!deviceId) {
		return emptyDeviceStateStore;
	}

	const cacheKey = `${deviceId}:${delay}`;
	let store = deviceStateWithDelayCache.get(cacheKey);
	if (!store) {
		store = createDeviceStateWithDelayStore(deviceId, delay);
		deviceStateWithDelayCache.set(cacheKey, store);
	}
	return store;
};

export const rebootRequired: Writable<Device> = writable(undefined);
